import { Form, Formik } from 'formik';
import React, { Suspense, useState } from 'react';
import styled from 'styled-components';
import Alert from '@zimpel/common/components/Alert/Alert';
import {
  BigBlock, HugeBlock, SmallBlock,
} from '@zimpel/common/components/Blocks';
import ButtonGroup from '@zimpel/common/components/ButtonGroup/ButtonGroup';
import { PrimaryButton } from '@zimpel/common/components/Buttons';
import { FormikTextinput } from '@zimpel/common/components/formikAdapters';
import { GridCol, GridRow } from '@zimpel/common/components/Grid';
import PasswordField from '@zimpel/common/components/PasswordField';
import { H2 } from '@zimpel/common/components/Headlines';
import Label from '@zimpel/common/components/Label';
import { AppLink } from '@zimpel/common/components/Links';
import { navigateTo } from '@zimpel/common/helpers/browser';
import t from '@zimpel/common/helpers/i18n';
import { ValidationError } from '@zimpel/common/newApi';
import { getConfig } from '@zimpel/common/config';
import useHandleErrors from '@zimpel/common/hooks/useHandleErrors';
import { login } from './api';
import Layout from './Layout';

const StyledLink = styled(AppLink)`
  margin-left: auto;
`;

const StyledSeperator = styled.div`
  display: flex;
  align-items: center;

  &::before {
    content: " ";
    flex: 1 1 auto;
    border-bottom: 1px solid var(--color-text-default);
  }

  &::after {
    content: " ";
    flex: 1 1 auto;
    border-bottom: 1px solid var(--color-text-default);
  }
`;

const StyledSeperatorContent = styled.span`
  padding: 0 10px;
  font-size: 14px;
`;

const StyledDpaHint = styled.div`
  margin-left: 20px;
  font-size: 14px;
  align-self: center;
`;

const DpaLoginBtn = React.lazy(() => import('./DpaLoginBtn'));

const Login = () => {
  const [globalError, setGlobalError] = useState<string | undefined>(undefined);
  const { theme } = getConfig();
  const handleErrors = useHandleErrors();

  return (
    <Layout>
      <GridRow>
        <GridCol $width={10} $padLeft={2}>
          <H2>{t('authentication.login.header')}</H2>

          {globalError && <Alert $type="error">{globalError}</Alert>}
          <Formik
            onSubmit={async (values) => {
              try {
                const { username, password } = values;
                const res = await login({ username, password });
                navigateTo(res.redirectPath);
              } catch (error) {
                if (error instanceof ValidationError) {
                  setGlobalError(t('authentication.login.credential_error'));
                } else {
                  handleErrors(() => {
                    throw error;
                  });
                }
              }
            }}
            initialValues={{
              username: '',
              password: '',
            }}
          >
            {({ isSubmitting }) => (
              <SmallBlock>
                <Form>
                  <Label htmlFor="username">
                    {t('authentication.username.label')}
                  </Label>
                  <FormikTextinput id="username" name="username" required autoFocus />

                  <SmallBlock>
                    <Label htmlFor="password">
                      {t('authentication.password.label')}
                    </Label>
                    <PasswordField id="password" required name="password" />
                  </SmallBlock>

                  <BigBlock>
                    <ButtonGroup>
                      <PrimaryButton disabled={isSubmitting} $width="s" $height="l" type="submit">
                        {t('authentication.login.button')}
                      </PrimaryButton>
                      <StyledLink to="/password-reset">{t('authentication.forgot_password.link')}</StyledLink>
                    </ButtonGroup>
                  </BigBlock>
                </Form>
              </SmallBlock>
            )}
          </Formik>

          {theme === 'zimpel' && (
            <>
              <HugeBlock>
                <StyledSeperator><StyledSeperatorContent>{t('authentication.login.or')}</StyledSeperatorContent></StyledSeperator>
              </HugeBlock>
              <HugeBlock>
                <Suspense fallback="">
                  <ButtonGroup>
                    <DpaLoginBtn>{t('authentication.login.button')}</DpaLoginBtn>
                    <StyledDpaHint>{t('authentication.login.dpa_id_hint')}</StyledDpaHint>
                  </ButtonGroup>
                </Suspense>
              </HugeBlock>
            </>
          )}

        </GridCol>
      </GridRow>
    </Layout>
  );
};

export default Login;
